import { useState, useEffect, useRef } from "react";
import styles from "./style.module.scss";
import { Icons } from "assets";
import classNames from "classnames";

interface OptionProps {
  id: number;
  value: string;
  label?: string;
  title: string;
  action?: () => void;
}

interface CustomSelectProps {
  label?: string;
  required?: boolean;
  options: OptionProps[];
  defaultActiveItem?: string;
  onChange?: (selected: OptionProps) => void;
  value?: string;
  error?: string;
}

const CustomSelect = ({
  options,
  label,
  required,
  defaultActiveItem = "Choose an option",
  onChange,
  value,
  error,
}: CustomSelectProps) => {
  const [openSelection, setOpenSelection] = useState(false);
  const [activeItem, setActiveItem] = useState<string | undefined>(
    defaultActiveItem
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const selectedOption = options.find((option) => option.value === value);
    setActiveItem(
      selectedOption?.title || selectedOption?.label || defaultActiveItem
    );
  }, [value, options, defaultActiveItem]);

  const handleOptionClick = (option: OptionProps) => {
    setActiveItem(option.title || option.label);
    if (onChange) {
      onChange(option);
    }
    setOpenSelection(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenSelection(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className={classNames(
        styles.selectWrapper,
        "flex flex-col gap-2 items-start justify-center"
      )}
    >
      <div
        ref={dropdownRef}
        className={classNames(styles.sortContainer, "w-full")}
      >
        {label && (
          <label className={styles.inputLabel}>
            {label}{" "}
            {required && (
              <label className={classNames(styles.asterik)}>*</label>
            )}
          </label>
        )}
        <div
          className={classNames(
            styles.sortLabel,
            "flex items-center justify-between cursor-pointer"
          )}
          onClick={() => setOpenSelection(!openSelection)}
        >
          <div className="flex items-center justify-between w-full gap-1">
            <span className={classNames(styles.activeItem)}>{activeItem}</span>
            <Icons.ChevDown />
          </div>
        </div>

        {openSelection && (
          <div className={classNames(styles.dropdown)}>
            {options.map((option) => (
              <div
                key={option.id}
                className={classNames(styles.optionItem, {
                  [styles.active]: option.value === value,
                })}
                onClick={() => handleOptionClick(option)}
              >
                <p>{option.title || option.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>
      {error && <div className="error">{error}</div>}
    </div>
  );
};

export default CustomSelect;
